import React from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  createColumnHelper,
  getFilteredRowModel,
  getExpandedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
} from "@tanstack/react-table";
import { Table } from "components/table/Table";
import { css, cx } from "../../../styled-system/css";
import { Flex, HStack } from "../../../styled-system/jsx";
import { RT8Pagination } from "components/table/Pagination";
import { useAppSelector } from "../../redux/hooks";
import { Button, buttonRecipe } from "components/core/basic/Button";
import { SearchInput } from "components/fields/base/TextField";
import { Select } from "components/fields/base/Select";
import type { HubspotTicket } from "zodios/apiDefinitions/api/tickets";
import NoteCard from "./NoteCard";
import { HUBSPOT_ORG_ID, HUBSPOT_URL } from "./constants";
const columnHelper = createColumnHelper<HubspotTicket>();

const columns = [
  columnHelper.accessor("ticket_id", {
    header: "Ticket ID",
    cell: (info) => {
      const ticketId = info.getValue();
      return (
        <a
          href={`${HUBSPOT_URL}/contacts/${HUBSPOT_ORG_ID}/record/0-5/${ticketId}`}
          className={cx(buttonRecipe({ variant: "link" }), css({ textDecoration: "none", fontSize: "small" }))}
        >
          {ticketId}
        </a>
      );
    },
    size: 100,
  }),
  columnHelper.accessor((row) => row.company.organization.id, {
    header: "org ID",
    id: "org_id",
    size: 90,
  }),
  columnHelper.accessor("subject", {
    header: "Subject",
    size: 250,
  }),

  columnHelper.accessor("priority", {
    header: "Priority",
    size: 100,
    cell: (info) => info.getValue().toUpperCase(),
  }),
  columnHelper.accessor("status", {
    header: "status",
  }),
  columnHelper.accessor((row) => row.company.name, {
    header: "Company",
    id: "company",
  }),
  columnHelper.accessor((row) => row.company.organization.org_type, {
    header: "ORG Type",
    id: "orgtype",
    size: 100,
  }),

  columnHelper.accessor((row) => `${row.contact.first_name} ${row.contact.last_name}`.trim(), {
    id: "contact_name",
    header: "Contact Name",
  }),

  columnHelper.accessor((row) => row.contact.email, {
    id: "contact_email",
    header: "Contact Email",
    size: 250,
  }),
  columnHelper.accessor((row) => `${row.owner.first_name} ${row.owner.last_name}`.trim(), {
    id: "owner_name",
    header: "Owner Name",
  }),

  columnHelper.accessor((row) => row.owner.email, {
    id: "owner_email",
    header: "Owner Email",
    size: 150,
  }),
  columnHelper.accessor((row) => row.owner.user?.id ?? "undefined", {
    id: "Owner ID",
    header: "owner_id",
    size: 30,
  }),
];

interface HubspotTableProps {
  tickets: HubspotTicket[];
}

const filters = [
  { id: "company", label: "Organization" },
  { id: "status", label: "Status" },
  { id: "orgtype", label: "Org Type" },
  { id: "priority", label: "Priority" },
];

const HubspotTable: React.FC<HubspotTableProps> = ({ tickets }) => {
  const user = useAppSelector((state) => state.user);
  const ownername = user.user_full_name.trim().toLowerCase();
  const table = useReactTable<HubspotTicket>({
    data: tickets,
    columns,
    enableRowSelection: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    initialState: {
      pagination: {
        pageSize: 20,
      },
    },
  });
  const createSelectItems = (columnId: string, defaultLabel: string): { label: string; value: string }[] => {
    const col = table.getColumn(columnId);
    const values = col?.getFacetedUniqueValues().keys();
    const selectItems = Array.from(values ?? []).map((val) => ({ label: String(val), value: String(val) }));

    return [{ label: defaultLabel, value: "All" }, ...selectItems];
  };

  const selectFilters = filters.map((filter) => ({
    items: createSelectItems(filter.id, filter.label),
    columnId: filter.id,
    label: filter.label,
  }));
  const handleMyTicketsToggle = () => {
    const isFiltered = table.getState().globalFilter === ownername;
    table.setGlobalFilter(isFiltered ? "" : ownername);
  };
  const resetFilters = () => {
    table.setGlobalFilter("");
    table.setColumnFilters([]);
  };

  return (
    <Flex className={css({ width: "100%", marginBottom: "1rem" })} direction="column" alignItems="stretch">
      <HStack
        className={css({ borderBottom: "1px solid token(colors.gray.200)", margin: "1rem", paddingBottom: "1rem" })}
        gap="4"
        alignItems="flex-end"
      >
        <Button onPress={handleMyTicketsToggle}>
          {table.getState().globalFilter === ownername ? "Show All Tickets" : "Filter My Tickets"}
        </Button>
        {selectFilters.map(({ items, columnId, label }) => (
          <Select
            key={columnId}
            aria-label={label}
            items={items}
            selectedKey={(table.getColumn(columnId)?.getFilterValue() ?? "All") as string}
            onSelectionChange={(key) => table.getColumn(columnId)?.setFilterValue(key === "All" ? undefined : key)}
          />
        ))}
        <SearchInput
          aria-label="Search..."
          value={table.getState().globalFilter ?? ""}
          placeholder={"Search..."}
          onChange={(val) => {
            table.setGlobalFilter(val);
          }}
          className={css({ minWidth: "250px" })}
        />
        <Button
          onPress={resetFilters}
          isDisabled={table.getState().globalFilter === "" && table.getState().columnFilters.length === 0}
        >
          Reset Filters
        </Button>
      </HStack>
      <div className={css({ width: "100%", overflowX: "auto" })}>
        <Table
          table={table}
          renderExpander={(row) => (
            <div
              className={css({
                width: "100%",
                padding: "large",
                backgroundColor: "#efefef",
              })}
            >
              <NoteCard
                notes={row.original.notes}
                tasks={row.original.tasks}
                reviews={row.original.bot_responses}
                content={row.original.content}
              />
            </div>
          )}
        />
      </div>
      <Flex className={css({ marginTop: "1rem" })} justifyContent={"flex-end"}>
        <RT8Pagination table={table} canSetPageSize={false} />
      </Flex>
    </Flex>
  );
};

export default HubspotTable;
